import { useMemo, useState } from 'react';

import { BasicFilterContainer } from '../cdl/Filter/BasicFilterContainer/BasicFilterContainer';
import { FilterText } from '../cdl/Filter/FilterText/FilterText';
import { TableBuilder } from '../cdl/TableBuilder/TableBuilder';
import { TableBuilderColumn } from '../cdl/TableBuilder/TableBuilderColumn';
import { Text } from '../cdl/Text/Text';
import { OnePortProductCatalogueProductMessage } from '../common/api/clients/one-port.api';
import { translate } from '../common/helpers/translate.helper';
import { Box } from '../common/ui/Box';

interface OnePortProductCatalogueTableProps {
    products: OnePortProductCatalogueProductMessage[];
}

interface ProductFilters {
    productId: string;
    productName: string;
    mpId: string;
    mpName: string;
}

const initialFilters: ProductFilters = {
    productId: '',
    productName: '',
    mpId: '',
    mpName: '',
};

export const OnePortProductCatalogueTable = ({ products }: OnePortProductCatalogueTableProps) => {
    const [filters, setFilters] = useState(initialFilters);
    const filteredProducts = useMemo(
        () =>
            products.filter((product) =>
                Object.entries(filters).every(([field, filter]) =>
                    product[field as keyof ProductFilters].toLowerCase().includes(filter.trim().toLowerCase())
                )
            ),
        [filters, products]
    );

    const updateFilter = (field: keyof ProductFilters, value: string) => {
        setFilters((currentFilters) => ({ ...currentFilters, [field]: value }));
    };

    return (
        <Box display="flex" flexDirection="column" gap={16}>
            <BasicFilterContainer
                hasActiveFilters={Object.values(filters).some((filter) => filter.length > 0)}
                onClear={() => setFilters(initialFilters)}
            >
                <FilterText
                    label={translate('integrationsOverview.productCatalogue.productId')}
                    filterByLabel={translate('integrationsOverview.productCatalogue.filterByProductId')}
                    value={filters.productId}
                    onChange={(value) => updateFilter('productId', value ?? '')}
                />
                <FilterText
                    label={translate('integrationsOverview.productCatalogue.productName')}
                    filterByLabel={translate('integrationsOverview.productCatalogue.filterByProductName')}
                    value={filters.productName}
                    onChange={(value) => updateFilter('productName', value ?? '')}
                />
                <FilterText
                    label={translate('integrationsOverview.productCatalogue.mpId')}
                    filterByLabel={translate('integrationsOverview.productCatalogue.filterByMpId')}
                    value={filters.mpId}
                    onChange={(value) => updateFilter('mpId', value ?? '')}
                />
                <FilterText
                    label={translate('integrationsOverview.productCatalogue.mpName')}
                    filterByLabel={translate('integrationsOverview.productCatalogue.filterByMpName')}
                    value={filters.mpName}
                    onChange={(value) => updateFilter('mpName', value ?? '')}
                />
            </BasicFilterContainer>

            {filteredProducts.length === 0 ? (
                <Text>{translate('integrationsOverview.productCatalogue.noResults')}</Text>
            ) : (
                <Box overflowX="auto">
                    <Box minWidth="760px">
                        <TableBuilder<OnePortProductCatalogueProductMessage> data={filteredProducts}>
                            <TableBuilderColumn<OnePortProductCatalogueProductMessage>
                                width="15%"
                                header={translate('integrationsOverview.productCatalogue.productId')}
                            >
                                {(product) => product.productId}
                            </TableBuilderColumn>
                            <TableBuilderColumn<OnePortProductCatalogueProductMessage>
                                width="35%"
                                header={translate('integrationsOverview.productCatalogue.productName')}
                            >
                                {(product) => product.productName}
                            </TableBuilderColumn>
                            <TableBuilderColumn<OnePortProductCatalogueProductMessage>
                                width="15%"
                                header={translate('integrationsOverview.productCatalogue.mpId')}
                            >
                                {(product) => product.mpId}
                            </TableBuilderColumn>
                            <TableBuilderColumn<OnePortProductCatalogueProductMessage>
                                width="35%"
                                header={translate('integrationsOverview.productCatalogue.mpName')}
                            >
                                {(product) => product.mpName}
                            </TableBuilderColumn>
                        </TableBuilder>
                    </Box>
                </Box>
            )}
        </Box>
    );
};
